import type Mark from "mark.js";
import type { Article } from "modules/documents/types";
import { type Accessor, createEffect, createMemo, createSignal, onCleanup, onMount } from "solid-js";
import { useDocumentContext } from "../../../providers/context";
import { createFrameContent } from "../helpers";

interface ContentFrameProps {
	class?: string;
	onHeightChange?: (height: number) => void;
	article: Accessor<Article | undefined>;
}

type MarkedWindow = Window & { Mark: typeof Mark };

export function ContentFrame(props: ContentFrameProps) {
	const { handleLinkClick, assets, searches } = useDocumentContext();

	let articleFrame: HTMLIFrameElement | undefined;

	const [innerElement, setInnerElement] = createSignal<HTMLElement>();
	const [targetHeight, setTargetHeight] = createSignal(0);

	const supressIFrameErrors = (event: Event) => {
		event.preventDefault();
		event.stopPropagation();
		console.error("An error occurred in the iframe", event);
		return false;
	};

	const onLinkClick = (e: MessageEvent) => handleLinkClick(e);

	onMount(() =>
		setTimeout(() => {
			if (!articleFrame) {
				return;
			}

			articleFrame.addEventListener("load", () => {
				const window = articleFrame.contentWindow;

				if (!window) {
					throw new Error("No window found in iframe");
				}

				const contentElement = window.document.getElementById("article-content");
				const protectedElement = window.document.getElementById("article-protected");

				if (!contentElement) {
					setInnerElement(undefined);

					if (!protectedElement) {
						throw new Error("No content or protected element found in iframe");
					}

					new ResizeObserver(() => {
						const newHeight = protectedElement.clientHeight + 12;
						setTargetHeight(newHeight);
						props.onHeightChange?.(newHeight);
					}).observe(protectedElement);

					return;
				}

				setInnerElement(contentElement);

				new ResizeObserver(() => {
					const newHeight = contentElement.clientHeight + 12;
					setTargetHeight(newHeight);
					props.onHeightChange?.(newHeight);
				}).observe(contentElement);
			});

			window.addEventListener("message", onLinkClick);
		}),
	);

	onCleanup(() => {
		window.removeEventListener("message", onLinkClick);
	});

	createEffect(() => {
		const target = innerElement();
		const files = assets();
		const article = props.article();
		const searched = searches();

		if (!target || !article || !articleFrame) {
			return;
		}

		const window = articleFrame.contentWindow as MarkedWindow | null;

		if (!window) {
			return;
		}

		let articleText = article.content;

		let filteredAssets = files.filter((asset) => !asset.fileName.endsWith("css"));
		filteredAssets = filteredAssets.filter((asset) => !asset.fileName.endsWith("js"));

		for (const asset of filteredAssets) {
			const ASSET_REGEX = new RegExp(`asset://${asset.fileName}`, "g");

			articleText = articleText.replace(ASSET_REGEX, asset.storageUrl);
		}

		target.innerHTML = articleText;

		const mark = new window.Mark(target);
		mark.mark(searched, { separateWordSearch: false });
	});

	const style = createMemo(() =>
		assets()
			.filter((asset) => asset.fileName.endsWith("css"))
			.map((asset) => `<link rel="stylesheet" href="${asset.storageUrl}" />`)
			.join("\n"),
	);

	const script = createMemo(() =>
		assets()
			.filter((asset) => asset.fileName.endsWith("js"))
			.map((asset) => `<script src="${asset.storageUrl}"></script>`)
			.join("\n"),
	);

	const content = createMemo(() => createFrameContent(style(), script(), props.article()?.isProtected || false));

	return (
		<iframe
			class={props.class}
			ref={articleFrame}
			srcdoc={content()}
			onError={supressIFrameErrors}
			style={{
				border: "none",
				height: `${targetHeight()}px`,
				width: "100%",
				"z-index": 1,
			}}
		/>
	);
}
